import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { CONSTRUCTION_SITE_PHOTOS } from '../../data/constructionSitePhotos';

const heroImage = CONSTRUCTION_SITE_PHOTOS[0];
const aboutImage = CONSTRUCTION_SITE_PHOTOS[2];

const SERVICES_PREVIEW = [
  { name: 'Pre-construction', desc: 'Buildability, scope alignment, and planning', to: '/services/pre-construction' },
  { name: 'New build', desc: 'Residential and commercial delivery', to: '/services/new-build' },
  { name: 'Commercial fit-out', desc: 'Base build and phased interiors', to: '/services/commercial-fit-out' },
  { name: 'Structural works', desc: 'Alterations, envelope, and coordination', to: '/services/structural-works' },
  { name: 'Interior fit-out', desc: 'Finishes and quality benchmarks', to: '/services/interior-fit-out' },
  { name: 'Design coordination', desc: 'RFIs, communication, change control', to: '/services/design-coordination' },
  { name: 'Site management', desc: 'QA, snagging, and handover', to: '/services/site-management-handover' },
];

const WHY_ARCHITECTS = [
  { title: 'Buildability', desc: 'Practical feedback during design development.' },
  { title: 'Communication', desc: 'Clear RFIs and structured site meetings.' },
  { title: 'Design intent', desc: 'We protect the details that matter.' },
  { title: 'Coordination', desc: 'Reliable trade sequencing and management.' },
  { title: 'Quality', desc: 'Inspections and benchmarks throughout.' },
  { title: 'Programme', desc: 'Transparent timelines and honest updates.' },
];

const PROCESS = [
  { step: '01', title: 'Pre-construction review' },
  { step: '02', title: 'Pricing and scope alignment' },
  { step: '03', title: 'Site mobilisation' },
  { step: '04', title: 'Delivery and coordination' },
  { step: '05', title: 'QA, completion, and handover' },
];

function Home() {
  const scrollContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <main>
      <Helmet>
        <title>MA Renovations | Construction for Architects & Developers</title>
        <meta
          name="description"
          content="Professional construction company delivering new build, structural, fit-out, and commercial projects. Glasgow and Scotland."
        />
      </Helmet>

      {/* Hero */}
      <div className="pt-36 sm:pt-44 pb-20 px-6 max-w-5xl mx-auto text-center">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-bold tracking-tight leading-tight">
          Construction partner for<br />architects and developers
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-white/70 font-light max-w-2xl mx-auto leading-relaxed">
          We deliver construction projects with a focus on buildability, coordination, and quality — from pre-construction through handover.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <button type="button" onClick={scrollContact}
            className="px-8 py-3 bg-accent text-white font-semibold rounded hover:bg-accent-dark transition-colors">
            Discuss a project
          </button>
          <button type="button" onClick={scrollContact}
            className="px-8 py-3 border border-white/40 text-white rounded hover:border-white transition-colors">
            Send us your drawings
          </button>
        </div>
      </div>

      {/* Construction photos */}
      {heroImage && (
      <section className="bg-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CONSTRUCTION_SITE_PHOTOS.slice(0, 8).map((src, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded">
                <img src={src} alt={`Site photography ${i + 1}`}
                  className="w-full h-full object-cover" loading={i < 4 ? 'eager' : 'lazy'} />
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/projects/construction"
              className="text-accent font-semibold hover:text-accent-dark transition-colors">
              View all project photography →
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* About teaser */}
      <section className="bg-slate-50 py-16 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {aboutImage && (
          <div className="aspect-[4/3] overflow-hidden rounded">
            <img className="w-full h-full object-cover" src={aboutImage} alt="Construction site" />
          </div>
          )}
          <div>
            <h2 className="font-display text-3xl text-navy font-bold tracking-tight">Built for project delivery</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              MA Renovations is a construction company focused on new build, structural, and fit-out projects. We work with architects, consultants, and clients to translate design intent into coordinated site delivery.
            </p>
            <Link to="/about" className="inline-block mt-6 text-accent font-semibold hover:text-accent-dark">
              About us →
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl text-navy font-bold text-center">Services</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES_PREVIEW.map(({ name, desc, to }) => (
              <Link key={to} to={to} className="group block p-5 rounded border border-slate-200 hover:border-accent transition-colors">
                <h3 className="font-display font-semibold text-navy group-hover:text-accent transition-colors">{name}</h3>
                <p className="text-slate-500 text-sm mt-1">{desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="inline-block px-8 py-3 bg-navy text-white rounded font-semibold hover:bg-navy-light transition-colors">
              View all services
            </Link>
          </div>
        </div>
      </section>

      {/* Why architects */}
      <section className="bg-navy py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl text-white font-bold text-center">Why architects work with us</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_ARCHITECTS.map(({ title, desc }) => (
              <div key={title} className="p-5 rounded border border-white/10">
                <h3 className="font-display font-semibold text-white">{title}</h3>
                <p className="text-white/60 text-sm mt-1">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl text-navy font-bold text-center">Process</h2>
          <ol className="mt-10 space-y-6">
            {PROCESS.map(({ step, title }) => (
              <li key={step} className="flex items-center gap-4">
                <span className="font-display text-2xl text-accent font-bold w-10 shrink-0">{step}</span>
                <span className="text-slate-700 text-lg">{title}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-slate-50 py-12 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          <div>
            <p className="font-display text-3xl text-navy font-bold">16+</p>
            <p className="text-slate-500 text-sm mt-1">Years experience</p>
          </div>
          <div>
            <p className="font-display text-3xl text-navy font-bold">Multi-sector</p>
            <p className="text-slate-500 text-sm mt-1">Residential & commercial</p>
          </div>
          <div>
            <p className="font-display text-3xl text-navy font-bold">H&S</p>
            <p className="text-slate-500 text-sm mt-1">Site-safe culture</p>
          </div>
          <div>
            <p className="font-display text-3xl text-navy font-bold">Collaborative</p>
            <p className="text-slate-500 text-sm mt-1">Architect & consultant delivery</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-16 px-6 text-center">
        <h2 className="font-display text-2xl sm:text-3xl text-white font-bold">Ready to discuss your next project?</h2>
        <p className="mt-3 text-white/60 max-w-lg mx-auto">
          Send us drawings or programme constraints and we will respond with a clear next step.
        </p>
        <button type="button" onClick={scrollContact}
          className="mt-8 px-10 py-3 bg-accent text-white rounded font-semibold hover:bg-accent-dark transition-colors">
          Get in touch
        </button>
      </section>
    </main>
  );
}

export default Home;
